import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";

const ProjectFloorplans = () => {
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalImage, setModalImage] = useState("");
  const [isMobile, setIsMobile] = useState(false);

  const { id } = useParams();
  const projectId = id;

  const ImageURL = "https://dev.mithahomes.com/backend";

  // Check screen size for responsive behavior
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Fetch Data
  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      setError("Project ID is missing from the URL.");
      return;
    }

    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${ImageURL}/api/mainprojectprocess/projectsgetapi/${projectId}`,
        );

        if (response.data.success) {
          setProjectData(response.data.data);
          setActiveTab(0);
        } else {
          setError("Failed to fetch project details.");
        }
      } catch (e) {
        setError(`Error loading project: ${e.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId]);

  const handleNextFloorplan = () => {
    setActiveTab((prev) =>
      prev === projectData.floorplans.length - 1 ? 0 : prev + 1,
    );
  };

  const handlePrevFloorplan = () => {
    setActiveTab((prev) =>
      prev === 0 ? projectData.floorplans.length - 1 : prev - 1,
    );
  };

  const handleImageClick = (imageUrl) => {
    setModalImage(ImageURL + imageUrl);
    setShowModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setShowModal(false);
    setModalImage("");
    document.body.style.overflow = "auto";
  };

  const handleModalNavigation = (direction) => {
    if (direction === "next") {
      const nextIndex =
        activeTab === projectData.floorplans.length - 1 ? 0 : activeTab + 1;
      setActiveTab(nextIndex);
      setModalImage(ImageURL + projectData.floorplans[nextIndex].image);
    } else {
      const prevIndex =
        activeTab === 0 ? projectData.floorplans.length - 1 : activeTab - 1;
      setActiveTab(prevIndex);
      setModalImage(ImageURL + projectData.floorplans[prevIndex].image);
    }
  };

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === "Escape" && showModal) {
        closeModal();
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => document.removeEventListener("keydown", handleEscKey);
  }, [showModal]);

  // -------------------------------------
  // LOADING
  // -------------------------------------
  if (loading)
    return (
      <div
        style={{
          height: "50vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          className="spinner"
          style={{
            height: 50,
            width: 50,
            borderRadius: "50%",
            border: "4px solid transparent",
            borderTopColor: "#2563eb",
            borderBottomColor: "#2563eb",
            animation: "spin 1s linear infinite",
          }}
        ></div>
      </div>
    );

  // -------------------------------------
  // ERROR
  // -------------------------------------
  if (error)
    return (
      <div
        style={{
          padding: 16,
          margin: 16,
          backgroundColor: "#ffe4e6",
          border: "1px solid #fecdd3",
          color: "#b91c1c",
          textAlign: "center",
        }}
      >
        <strong>Error:</strong> {error}
      </div>
    );

  // -------------------------------------
  // NO DATA
  // -------------------------------------
  if (!projectData?.floorplans?.length) return <></>;

  const currentFloorplan = projectData.floorplans[activeTab];

  return (
    <>
      <div style={{ borderRadius: 12 }}>
        {/* TITLE */}
        <div style={{ textAlign: "center" }}>
          <h2
            style={{
              marginBottom: "0px",
              fontSize: "clamp(22px, 3vw, 30px)",
              fontWeight: 600,
              color: "#1f2937",
            }}
          >
            Floor Plans
          </h2>
        </div>

        {/* SCROLLING + CENTERED TABS */}
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            overflowX: "auto",
          }}
        >
          <div
            className="tabs-scroll-container scroll-tabs"
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: "0px",
              padding: "8px 16px",
              scrollbarWidth: "none",
              WebkitOverflowScrolling: "touch",
              scrollSnapType: "x mandatory",
            }}
          >
            {projectData.floorplans.map((floorplan, idx) => (
              <button
                key={idx}
                onClick={() => setActiveTab(idx)}
                className="floorplan-tab"
                style={{
                  flexShrink: 0,
                  padding: "12px 20px",
                  fontWeight: 550,
                  whiteSpace: "nowrap",
                  border: "none",
                  fontSize: 15,
                  cursor: "pointer",
                  scrollSnapAlign: "center",
                  backgroundColor: activeTab === idx ? "#e25d26" : "",
                  color: activeTab === idx ? "white" : "black",
                }}
              >
                {floorplan.name}
              </button>
            ))}
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div style={{ padding: "16px", paddingTop: "0px" }}>
          <div style={{ maxWidth: 1000, margin: "0 auto" }}>
            {/* FLOORPLAN IMAGE */}
            <div style={{ width: "100%" }}>
              <div
                className="image-wrapper"
                style={{
                  backgroundColor: "#fff",
                  borderRadius: 12,
                  padding: 16,
                  position: "relative",
                  width: "100%",
                  aspectRatio: "16/9",
                  cursor: "pointer",
                  overflow: "hidden",
                }}
                onClick={() => handleImageClick(currentFloorplan.image)}
              >
                <img
                  src={ImageURL + currentFloorplan.image}
                  alt={currentFloorplan.name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "scale-down",
                    transition: "transform 0.3s ease",
                  }}
                />

                {/* Zoom icon overlay */}
                <div
                  style={{
                    position: "absolute",
                    top: "10px",
                    right: "10px",
                    background: "rgba(0, 0, 0, 0.7)",
                    borderRadius: "50%",
                    width: "40px",
                    height: "40px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    opacity: 0.8,
                    transition: "opacity 0.3s ease",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    <line x1="11" y1="8" x2="11" y2="14"></line>
                    <line x1="8" y1="11" x2="14" y2="11"></line>
                  </svg>
                </div>

                {/* MOBILE NAVIGATION */}
                <div
                  className="nav-container-desktop-hidden"
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: 0,
                    right: 0,
                    display: "none",
                    justifyContent: "space-between",
                    padding: "0 16px",
                    transform: "translateY(-50%)",
                  }}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrevFloorplan();
                    }}
                    style={{
                      background: "#fff",
                      borderRadius: "50%",
                      border: "none",
                      width: 45,
                      height: 45,
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                    }}
                  >
                    ◀
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNextFloorplan();
                    }}
                    style={{
                      background: "#fff",
                      borderRadius: "50%",
                      border: "none",
                      width: 45,
                      height: 45,
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                    }}
                  >
                    ▶
                  </button>
                </div>
              </div>

              {/* Click hint text */}
              <p
                style={{
                  textAlign: "center",
                  marginTop: "12px",
                  color: "#666",
                  fontSize: "14px",
                  fontStyle: "italic",
                  display: isMobile ? "none" : "block",
                }}
              >
                Click on the image to view in full screen
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL FOR FULL-SCREEN IMAGE */}
      {showModal && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.95)",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: isMobile ? "0" : "20px",
            overflow: "hidden",
          }}
          onClick={closeModal}
        >
          {/* Modal header for mobile */}
          {isMobile && (
            <div
              style={{
                width: "100%",
                padding: "16px",
                background: "rgba(0,0,0,0.8)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                position: "absolute",
                top: 0,
                left: 0,
                zIndex: 10001,
              }}
            >
              <div
                style={{
                  color: "white",
                  fontSize: "16px",
                  fontWeight: "500",
                  paddingLeft: "10px",
                }}
              >
                {currentFloorplan.name}
              </div>
              <button
                onClick={closeModal}
                style={{
                  background: "#e25d26",
                  color: "white",
                  border: "none",
                  borderRadius: "50%",
                  width: "36px",
                  height: "36px",
                  fontSize: "18px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                ✕
              </button>
            </div>
          )}

          {/* Main modal content */}
          <div
            className="modal-content"
            style={{
              position: "relative",
              width: isMobile ? "100%" : "90%",
              height: isMobile ? "100%" : "90%",
              maxWidth: isMobile ? "100%" : "1200px",
              maxHeight: isMobile ? "100%" : "90vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button for desktop */}
            {!isMobile && (
              <button
                onClick={closeModal}
                style={{
                  position: "absolute",
                  top: "-20px",
                  right: "-20px",
                  background: "#e25d26",
                  color: "white",
                  border: "none",
                  borderRadius: "50%",
                  width: "44px",
                  height: "44px",
                  fontSize: "22px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 10000,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                }}
              >
                ✕
              </button>
            )}

            {/* Image container */}
            <div
              style={{
                width: "100%",
                height: isMobile ? "calc(100% - 60px)" : "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                overflow: "hidden",
                padding: isMobile ? "60px 10px 10px 10px" : "20px",
              }}
            >
              <img
                src={modalImage}
                alt="Floorplan full view"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  borderRadius: isMobile ? "0" : "8px",
                }}
              />
            </div>

            {/* Image info for desktop */}
            {!isMobile && (
              <div
                style={{
                  position: "absolute",
                  bottom: "20px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: "rgba(0,0,0,0.7)",
                  color: "white",
                  padding: "10px 20px",
                  borderRadius: "20px",
                  fontSize: "14px",
                  minWidth: "200px",
                  textAlign: "center",
                }}
              >
                {currentFloorplan.name}
              </div>
            )}

            {/* Navigation buttons for multiple floorplans */}
            {projectData.floorplans.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleModalNavigation("prev");
                  }}
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: isMobile ? "10px" : "30px",
                    transform: "translateY(-50%)",
                    background: "rgba(255, 255, 255, 0.95)",
                    border: "none",
                    borderRadius: "50%",
                    width: isMobile ? "44px" : "54px",
                    height: isMobile ? "44px" : "54px",
                    fontSize: isMobile ? "18px" : "22px",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 10000,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  }}
                  aria-label="Previous floorplan"
                >
                  ◀
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleModalNavigation("next");
                  }}
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: isMobile ? "10px" : "30px",
                    transform: "translateY(-50%)",
                    background: "rgba(255, 255, 255, 0.95)",
                    border: "none",
                    borderRadius: "50%",
                    width: isMobile ? "44px" : "54px",
                    height: isMobile ? "44px" : "54px",
                    fontSize: isMobile ? "18px" : "22px",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 10000,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  }}
                  aria-label="Next floorplan"
                >
                  ▶
                </button>
              </>
            )}

            {/* Pagination dots for mobile */}
            {isMobile && projectData.floorplans.length > 1 && (
              <div
                style={{
                  position: "absolute",
                  bottom: "20px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  gap: "8px",
                  zIndex: 10000,
                }}
              >
                {projectData.floorplans.map((_, idx) => (
                  <div
                    key={idx}
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      backgroundColor:
                        idx === activeTab ? "#e25d26" : "rgba(255,255,255,0.5)",
                      transition: "background-color 0.3s ease",
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Close hint for mobile */}
          {isMobile && (
            <div
              style={{
                position: "absolute",
                bottom: "10px",
                left: "50%",
                transform: "translateX(-50%)",
                color: "rgba(255,255,255,0.7)",
                fontSize: "12px",
                textAlign: "center",
                padding: "8px 16px",
                background: "rgba(0,0,0,0.5)",
                borderRadius: "20px",
              }}
            >
              Tap outside to close
            </div>
          )}
        </div>
      )}

      {/* GLOBAL CSS */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes slideUp {
            from { transform: translateY(20px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }

          .scroll-tabs::-webkit-scrollbar { display: none; }
          
          .image-wrapper {
            transition: transform 0.2s ease;
          }
          
          .image-wrapper:hover {
            transform: scale(1.01);
          }
          
          .image-wrapper:hover > div {
            opacity: 1;
          }
          
          .modal-overlay {
            animation: fadeIn 0.3s ease;
          }
          
          .modal-content {
            animation: slideUp 0.3s ease;
          }
          
          @media (max-width: 768px) {
            .nav-container-desktop-hidden { 
              display: flex !important; 
            }
            
            .image-wrapper { 
              aspect-ratio: 4/3 !important; 
            }
            
            .image-wrapper > div {
              width: 32px !important;
              height: 32px !important;
              top: 8px !important;
              right: 8px !important;
            }
            
            .image-wrapper > div svg {
              width: 16px !important;
              height: 16px !important;
            }
            
            .modal-content img {
              cursor: pointer;
            }
            
            /* Touch-friendly navigation buttons */
            button {
              -webkit-tap-highlight-color: transparent;
            }
          }
          
          @media (min-width: 769px) {
            .modal-content {
              max-width: 90% !important;
              max-height: 85vh !important;
            }
          }
          
          /* For very large screens */
          @media (min-width: 1200px) {
            .modal-content {
              max-width: 80% !important;
            }
          }
          
          /* For landscape mode on mobile */
          @media (max-width: 768px) and (orientation: landscape) {
            .modal-content {
              flex-direction: row !important;
            }
            
            .modal-content img {
              max-height: 80vh !important;
              max-width: 80% !important;
            }
            
            .modal-overlay {
              padding: 10px !important;
            }
          }
        `}
      </style>
    </>
  );
};

export default ProjectFloorplans;
